/**
 * User Management Screen Desktop
 * 
 * Desktop version of the user management interface for managers.
 * Provides comprehensive user management with filtering, sorting, and bulk actions.
 * 
 * @module UserManagementScreenDesktop
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Toaster } from 'react-hot-toast';
import {
  Filter,
  Download,
  RefreshCw,
  UserPlus,
  Users,
  Search,
  CheckCircle,
  XCircle,
  Key,
  Mail
} from 'lucide-react';
import { useUserManagement } from '../../hooks/useUserManagement';
import { useAuth } from '../../contexts/AuthContext';
import { UserTable } from '../../components/manager/UserTable';
import { UserForm } from '../../components/manager/UserForm';
import { ConfirmationModal } from '../../components/common/ConfirmationModal';
import { UserManagementService } from '../../services/UserManagementService';
import { UserProfile, UserFilters, UserSort, UserRole, CreateUserData, UpdateUserData, BulkUserAction } from '../../@types/user';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Store } from '../../@types/store';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.lg};
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => props.active ? ThemeColors.primary.light : 'white'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.primary};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
    color: ${ThemeColors.primary.main};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${ThemeColors.primary.main};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.primary.dark};
          }
        `;
      case 'success':
        return `
          background: ${ThemeColors.success.main};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.success.dark};
          }
        `;
      case 'danger':
        return `
          background: ${ThemeColors.error.main};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.error.dark};
          }
        `;
      default:
        return `
          background: white;
          color: ${ThemeColors.text.primary};
          border: 1px solid ${ThemeColors.border.main};
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.background.secondary};
            border-color: ${ThemeColors.primary.main};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BulkActionsBar = styled.div<{ visible: boolean }>`
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${ThemeColors.primary.light};
  border: 1px solid ${ThemeColors.primary.main};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const SelectedCount = styled.span`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.primary.main};
`;

const ErrorMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.dark};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const SuccessMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.success.light};
  color: ${ThemeColors.success.dark};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  padding: ${Spacing.lg};
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const StatTitle = styled.h3`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  margin: 0 0 ${Spacing.sm} 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const StatChange = styled.div<{ positive?: boolean }>`
  font-size: ${Typography.fontSize.xs};
  color: ${props => props.positive ? ThemeColors.success.main : ThemeColors.error.main};
  margin-top: ${Spacing.xs};
`;

/**
 * User Management Screen Desktop Component
 * 
 * Provides comprehensive user management interface including:
 * - Filtering and searching
 * - Bulk actions
 * - User creation/editing
 * - Role and permission management
 */
export const UserManagementScreenDesktop: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Use user management hook
  const {
    users,
    userStatistics,
    selectedUsers,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    performBulkAction,
    selectUser,
    selectAllUsers,
    clearSelection,
    clearError,
    refreshUsers
  } = useUserManagement();

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stores, setStores] = useState<Array<{ id: string; name: string; customerNo: string | number; salesGroup?: string }>>([]);
  const [salesGroups, setSalesGroups] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  
  // Confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  /**
   * Load stores on component mount
   */
  useEffect(() => {
    const loadStores = async () => {
      try {
        const storesRef = collection(db, 'Stores');
        const storesSnapshot = await getDocs(storesRef);
        const storesList = storesSnapshot.docs.map(doc => {
          const data = doc.data() as any; // Use any to access both salesGroup and SalesGroup
          return {
            id: doc.id,
            name: data.name || data.StoreName || 'Unnamed Store',
            customerNo: data.CustomerNo || data.customerNo || '', // Check both CustomerNo (Firestore) and customerNo
            salesGroup: data.SalesGroup || data.salesGroup || '' // Firestore uses capital S SalesGroup
          };
        });
        setStores(storesList);
        
        // Extract unique sales groups
        const uniqueSalesGroups = Array.from(
          new Set(
            storesList
              .map(store => store.salesGroup)
              .filter((sg): sg is string => !!sg && sg.trim() !== '')
          )
        ).sort();
        setSalesGroups(uniqueSalesGroups);
      } catch (error) {
        console.error('Error loading stores:', error);
      }
    };
    
    loadStores();
  }, []);

  /**
   * Load all unique roles from database (excluding admin)
   */
  useEffect(() => {
    const loadAvailableRoles = async () => {
      try {
        // Fetch all users to get unique roles
        const { users: allUsers } = await UserManagementService.getUsers({}, undefined, 1000);
        const uniqueRoles = Array.from(
          new Set(allUsers.map(user => user.role).filter(role => role !== 'admin'))
        ) as UserRole[];
        setAvailableRoles(uniqueRoles.sort());
      } catch (error) {
        console.error('Error loading available roles:', error);
        // Fallback: extract from currently loaded users
        if (users && users.length > 0) {
          const uniqueRoles = Array.from(
            new Set(users.map(user => user.role).filter(role => role !== 'admin'))
          ) as UserRole[];
          setAvailableRoles(uniqueRoles.sort());
        }
      }
    };
    
    loadAvailableRoles();
  }, []); // Only run once on mount

  /**
   * Also update roles when users change (as fallback)
   */
  useEffect(() => {
    if (users && users.length > 0 && availableRoles.length === 0) {
      const uniqueRoles = Array.from(
        new Set(users.map(user => user.role).filter(role => role !== 'admin'))
      ) as UserRole[];
      if (uniqueRoles.length > 0) {
        setAvailableRoles(uniqueRoles.sort());
      }
    }
  }, [users, availableRoles.length]);

  /**
   * Load users on component mount and when filters change
   */
  useEffect(() => {
    const filters: UserFilters = {};
    
    if (selectedRole !== 'all') {
      filters.role = selectedRole;
    }

    if (searchTerm) {
      filters.searchTerm = searchTerm;
    }

    fetchUsers(filters, { field: 'firstName', direction: 'asc' }, true);
  }, [selectedRole, searchTerm, fetchUsers]);

  /**
   * Clear success message after 5 seconds
   */
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * Handle user creation
   */
  const handleCreateUser = useCallback(async (userData: CreateUserData) => {
    const result = await createUser(userData);
    if (result.success) {
      setShowForm(false);
      setSuccessMessage('User created successfully!');
    } else {
      throw new Error(result.error || 'Failed to create user');
    }
  }, [createUser]);

  /**
   * Handle user editing
   */
  const handleEditUser = useCallback(async (userData: UpdateUserData) => {
    if (!editingUser) return;
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementScreenDesktop.tsx:442',message:'handleEditUser called',data:{userId:editingUser.id,hasPassword:!!userData.password,passwordLength:userData.password?.length,userDataKeys:Object.keys(userData)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    
    const result = await updateUser(editingUser.id, userData);
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementScreenDesktop.tsx:448',message:'updateUser result',data:{userId:editingUser.id,success:result.success,error:result.error},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    
    if (result.success) {
      setShowForm(false);
      setEditingUser(undefined);
      setSuccessMessage('User updated successfully!');
    } else {
      throw new Error(result.error || 'Failed to update user');
    }
  }, [updateUser, editingUser]);

  /**
   * Handle user deletion - show confirmation modal
   */
  const handleDeleteUserClick = useCallback((userToDelete: UserProfile) => {
    setUserToDelete(userToDelete);
    setShowDeleteConfirm(true);
  }, []);

  /**
   * Confirm user deletion
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;
    
    const result = await deleteUser(userToDelete.id);
    setShowDeleteConfirm(false);
    setUserToDelete(null);
    
    if (result.success) {
      setSuccessMessage('User deleted successfully!');
    }
  }, [userToDelete, deleteUser]);

  /**
   * Handle password reset
   */
  const handleResetPassword = useCallback(async (userToReset: UserProfile) => {
    const result = await resetUserPassword(userToReset.id);
    if (result.success) {
      setSuccessMessage('Password reset email sent successfully!');
    }
  }, [resetUserPassword]);

  /**
   * Handle bulk actions
   */
  const handleBulkAction = useCallback(async (action: string) => {
    if (selectedUsers.length === 0) return;

    let bulkAction: BulkUserAction;
    
    switch (action) {
      case 'activate':
        bulkAction = { userIds: selectedUsers, action: 'activate' };
        break;
      case 'deactivate':
        bulkAction = { userIds: selectedUsers, action: 'deactivate' };
        break;
      case 'delete':
        setShowBulkDeleteConfirm(true);
        return; // Will be handled by confirmation modal
      case 'resetPassword':
        bulkAction = { userIds: selectedUsers, action: 'resetPassword' };
        break;
      default:
        return;
    }

    const results = await performBulkAction(bulkAction);
    const successCount = results.filter(r => r.success).length;
    
    if (successCount > 0) {
      setSuccessMessage(`${successCount} users updated successfully!`);
      clearSelection();
    }
  }, [selectedUsers, performBulkAction, clearSelection]);

  /**
   * Confirm bulk delete
   */
  const handleConfirmBulkDelete = useCallback(async () => {
    if (selectedUsers.length === 0) {
      setShowBulkDeleteConfirm(false);
      return;
    }

    const bulkAction: BulkUserAction = { 
      userIds: selectedUsers, 
      action: 'delete' 
    };
    
    const results = await performBulkAction(bulkAction);
    const successCount = results.filter(r => r.success).length;
    
    setShowBulkDeleteConfirm(false);
    
    if (successCount > 0) {
      setSuccessMessage(`${successCount} users deleted successfully!`);
      clearSelection();
    }
  }, [selectedUsers, performBulkAction, clearSelection]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    refreshUsers();
  }, [refreshUsers]);

  /**
   * Open create user form
   */
  const openCreateForm = useCallback(() => {
    setEditingUser(undefined);
    setShowForm(true);
  }, []);

  /**
   * Open edit user form
   */
  const openEditForm = useCallback((userToEdit: UserProfile) => {
    setEditingUser(userToEdit);
    setShowForm(true);
  }, []);

  /**
   * Close form
   */
  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditingUser(undefined);
  }, []);

  return (
    <PageContainer isMenuCollapsed={false}>
      <Toaster position="top-right" />
      <MainContent>
        <Header>
          <Title>User Management</Title>
        </Header>

        {/* Statistics */}
        {userStatistics && (
          <StatsGrid>
            <StatCard>
              <StatTitle>Total Users</StatTitle>
              <StatValue>{userStatistics.totalUsers}</StatValue>
            </StatCard>
            <StatCard>
              <StatTitle>Active Users</StatTitle>
              <StatValue>{userStatistics.activeUsers}</StatValue>
            </StatCard>
            <StatCard>
              <StatTitle>Managers</StatTitle>
              <StatValue>{userStatistics.usersByRole.manager}</StatValue>
            </StatCard>
            <StatCard>
              <StatTitle>Sales Reps</StatTitle>
              <StatValue>{userStatistics.usersByRole['sales-representative']}</StatValue>
            </StatCard>
          </StatsGrid>
        )}

        {/* Success Message */}
        {successMessage && (
          <SuccessMessage>
            {successMessage}
            <Button variant="secondary" onClick={() => setSuccessMessage(null)} style={{ marginLeft: Spacing.md }}>
              Dismiss
            </Button>
          </SuccessMessage>
        )}

        {/* Error Display */}
        {error && (
          <ErrorMessage>
            {error}
            <Button variant="secondary" onClick={clearError} style={{ marginLeft: Spacing.md }}>
              Dismiss
            </Button>
          </ErrorMessage>
        )}

        {/* Toolbar */}
        <Toolbar>
          <ToolbarSection>
            <SearchBar>
              <Search />
              <SearchInput
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            
            <FilterGroup>
              <FilterButton
                active={selectedRole === 'all'}
                onClick={() => setSelectedRole('all')}
              >
                All Roles
              </FilterButton>
              {availableRoles.map(role => {
                const roleLabels: Record<UserRole, string> = {
                  'manager': 'Managers',
                  'sales-representative': 'Sales Reps',
                  'hand-lead': 'Hand Lead',
                  'admin': 'Admins' // This won't be shown since we filter it out
                };
                return (
                  <FilterButton
                    key={role}
                    active={selectedRole === role}
                    onClick={() => setSelectedRole(role)}
                  >
                    {roleLabels[role] || role}
                  </FilterButton>
                );
              })}
            </FilterGroup>

          </ToolbarSection>

          <ToolbarSection>
            <Button variant="secondary" onClick={handleRefresh}>
              <RefreshCw />
              Refresh
            </Button>
            <Button variant="secondary">
              <Download />
              Export
            </Button>
            <Button variant="primary" onClick={openCreateForm}>
              <UserPlus />
              Add User
            </Button>
          </ToolbarSection>
        </Toolbar>

        {/* Bulk Actions Bar */}
        <BulkActionsBar visible={selectedUsers.length > 0}>
          <SelectedCount>
            {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
          </SelectedCount>
          <ToolbarSection>
            <Button variant="success" onClick={() => handleBulkAction('activate')}>
              <CheckCircle />
              Activate Selected
            </Button>
            <Button variant="danger" onClick={() => handleBulkAction('deactivate')}>
              <XCircle />
              Deactivate Selected
            </Button>
            <Button variant="secondary" onClick={() => handleBulkAction('resetPassword')}>
              <Key />
              Reset Passwords
            </Button>
            <Button variant="danger" onClick={() => handleBulkAction('delete')}>
              <XCircle />
              Delete Selected
            </Button>
            <Button variant="secondary" onClick={clearSelection}>
              Clear Selection
            </Button>
          </ToolbarSection>
        </BulkActionsBar>

        {/* Users Table */}
        <UserTable
          users={users}
          isLoading={isLoading}
          onEdit={openEditForm}
          onDelete={handleDeleteUserClick}
          onResetPassword={handleResetPassword}
          selectable
          selectedUserIds={selectedUsers}
          onSelectionChange={(userIds) => {
            // Handle selection change
            selectedUsers.forEach(id => {
              if (!userIds.includes(id)) {
                selectUser(id);
              }
            });
            userIds.forEach(id => {
              if (!selectedUsers.includes(id)) {
                selectUser(id);
              }
            });
          }}
          showActions
        />

        {/* User Form Modal */}
        {showForm && (
          <UserForm
            user={editingUser}
            mode={editingUser ? 'edit' : 'create'}
            isLoading={isCreating || isUpdating}
            onSubmit={editingUser ? handleEditUser : handleCreateUser}
            onCancel={closeForm}
            departments={['Sales', 'Management', 'Support']}
            salesGroups={salesGroups}
            stores={stores}
          />
        )}

        {/* Delete User Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          title="Delete User"
          message={`Are you sure you want to delete ${userToDelete?.displayName || 'this user'}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setUserToDelete(null);
          }}
          isLoading={isDeleting}
        />

        {/* Bulk Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showBulkDeleteConfirm}
          title="Delete Users"
          message={`Are you sure you want to delete ${selectedUsers.length} user(s)? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={handleConfirmBulkDelete}
          onCancel={() => setShowBulkDeleteConfirm(false)}
          isLoading={isDeleting}
        />
      </MainContent>
    </PageContainer>
  );
};